import { useEffect, useState } from "react";

/**
 * Hook que detecta si el viewport actual coincide con una media query.
 * Re-renderiza el componente cuando cambia (resize, rotación de cel, etc).
 *
 * Uso típico:
 *   const isMobile = useMediaQuery("(max-width: 1023px)");
 *   const isDesktop = useMediaQuery("(min-width: 1024px)");
 *
 * SSR-safe: en el primer render del server devuelve `false`.
 * En el primer render del cliente se actualiza inmediatamente al valor real.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Set valor inicial (por si cambió entre el useState y el useEffect)
    setMatches(mql.matches);

    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

/**
 * Helper que mapea los breakpoints estándar de Tailwind a queries.
 * Más legible y consistente que escribir "(min-width: 1024px)" en cada componente.
 *
 * Uso:
 *   const isLg = useBreakpoint("lg");      // true si viewport >= 1024px
 *   const isLgDown = useBreakpoint("lg-"); // true si viewport < 1024px (mobile/tablet)
 */
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;
type BreakpointDirection = Breakpoint | `${Breakpoint}-`;

export function useBreakpoint(bp: BreakpointDirection): boolean {
  const isDown = bp.endsWith("-");
  const key = (isDown ? bp.slice(0, -1) : bp) as Breakpoint;
  const px = BREAKPOINTS[key];
  const query = isDown ? `(max-width: ${px - 1}px)` : `(min-width: ${px}px)`;
  return useMediaQuery(query);
}

/**
 * Helper específico para "estoy en mobile/tablet" (< lg).
 * Es el caso de uso más común: cambiar entre layout móvil y desktop.
 */
export function useIsMobile(): boolean {
  return useBreakpoint("lg-");
}
